import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
    Link2, Copy, Check, Users, Crown, Eye, Pencil,
    Trash2, Loader2, ShieldCheck, UserMinus, Share2, Mail,
    Lock, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

// Discoverability options - independent of collab sharing. Drives whether
// the category surfaces on the owner's public profile (`/{username}`) and
// on the public-category landing page (`/c/{category_id}`).
const VISIBILITY_OPTIONS = [
    {
        value: 'private',
        label: 'Private',
        icon: Lock,
        caption: 'Only you can see this category.',
    },
    {
        value: 'unlisted',
        label: 'Unlisted',
        icon: Link2,
        caption: 'Anyone with the link can view. Not on your profile.',
    },
    {
        value: 'public',
        label: 'Public',
        icon: Globe,
        caption: 'Shows on your public profile. Anyone can find it.',
    },
];

function ShareCategoryModal({ open, onOpenChange, category, userTier, onVisibilityChange, onRequestEdit }) {
    const [loading, setLoading] = useState(false);
    const [shareInfo, setShareInfo] = useState(null);
    const [copied, setCopied] = useState(false);
    const [editPermission, setEditPermission] = useState(false);
    const [visibility, setVisibility] = useState(category?.share_visibility || 'private');
    const [savingVisibility, setSavingVisibility] = useState(false);

    const isPro = userTier && userTier !== 'free';

    const fetchShareInfo = useCallback(async () => {
        if (!category) return;
        const url = `${API}/categories/${category.category_id}/collab`;
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                if (attempt > 0) await new Promise(r => setTimeout(r, 1000));
                const res = await fetch(url, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setShareInfo(data);
                    setEditPermission(data.default_permission === 'edit');
                    return;
                }
                setShareInfo(null);
                return;
            } catch {
                if (attempt === 0) continue;
                setShareInfo(null);
            }
        }
    }, [category]);

    useEffect(() => {
        // All setState calls deferred to a microtask so React 19's
        // set-state-in-effect heuristic doesn't flag this effect.
        const id = setTimeout(() => {
            if (open && category) {
                setVisibility(category.share_visibility || 'private');
                fetchShareInfo();
            } else {
                setShareInfo(null);
                setCopied(false);
            }
        }, 0);
        return () => clearTimeout(id);
    }, [open, category, fetchShareInfo]);

    const handleChangeVisibility = async (newVis) => {
        if (!category?.category_id || newVis === visibility) return;
        setSavingVisibility(true);
        try {
            const res = await fetch(`${API}/categories/${category.category_id}/share`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ visibility: newVis }),
            });
            if (!res.ok) throw new Error(await res.text());
            setVisibility(newVis);
            // Let Dashboard re-render the sidebar "Public" badge etc.
            onVisibilityChange?.(newVis);
            toast.success(
                newVis === 'private' ? 'Hidden from your public profile' :
                newVis === 'unlisted' ? 'Anyone with the link can view this collection' :
                'Now visible on your public profile'
            );
        } catch (err) {
            toast.error('Could not update visibility');
        } finally {
            setSavingVisibility(false);
        }
    };

    const handleCreateShare = async () => {
        if (!category?.category_id) {
            toast.error('No collection selected');
            return;
        }
        setLoading(true);
        const permission = editPermission ? 'edit' : 'view';
        const url = `${API}/categories/${category.category_id}/collab?permission=${permission}`;

        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                if (attempt > 0) await new Promise(r => setTimeout(r, 1500));
                const res = await fetch(url, { method: 'POST', credentials: 'include' });
                if (!res.ok) {
                    let errMsg = `Error ${res.status}`;
                    try {
                        const err = await res.json();
                        errMsg = err.detail || errMsg;
                    } catch {
                        errMsg = `Server error (${res.status})`;
                    }
                    if (res.status === 429 && attempt === 0) continue;
                    toast.error(errMsg);
                    setLoading(false);
                    return;
                }
                toast.success('Share link created');
                fetchShareInfo();
                setLoading(false);
                return;
            } catch (err) {
                console.error(`Share attempt ${attempt + 1} failed:`, err, 'URL:', url);
                if (attempt === 0) continue;
                toast.error(`Error: ${err?.name || 'Unknown'} - ${err?.message || 'Request failed'}. URL: ${url}`);
            }
        }
        setLoading(false);
    };

    const handleUpdatePermission = async (newEdit) => {
        setEditPermission(newEdit);
        if (!shareInfo?.is_shared) return;
        const perm = newEdit ? 'edit' : 'view';
        try {
            const res = await fetch(`${API}/categories/${category.category_id}/collab?permission=${perm}`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!res.ok) {
                toast.error('Failed to update permission');
                return;
            }
            toast.success(`Default permission updated to ${newEdit ? 'edit' : 'view-only'}`);
            fetchShareInfo();
        } catch {
            toast.error('Failed to update permission');
        }
    };

    const handleRevokeShare = async () => {
        if (!window.confirm('Revoke sharing? All members will lose access.')) return;
        setLoading(true);
        try {
            await fetch(`${API}/categories/${category.category_id}/collab`, {
                method: 'DELETE',
                credentials: 'include',
            });
            toast.success('Share revoked');
            setShareInfo(null);
        } catch {
            toast.error('Failed to revoke share');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        const link = `${window.location.origin}/join/${shareInfo.share_token}`;
        const fullMessage = `Join my "${category?.name}" collection on BrainSync\n${link}`;
        navigator.clipboard.writeText(fullMessage);
        setCopied(true);
        toast.success('Invite link copied');
        setTimeout(() => setCopied(false), 2000);
    };

    // ── VIEW LINK (read-only) ────────────────────────────────────
    // Lightweight share URL that drops the recipient straight into the
    // public-category landing page. No sign-up CTA, no membership.
    // Only meaningful when the category is Unlisted or Public; for
    // Private categories the backend returns 404 from /api/public/categories/{id},
    // so the modal nudges the user to switch visibility instead.
    //
    // NOTE: we share the clean `/c/{id}` URL (not the SSR shell at
    // `/api/c/{id}`) because WhatsApp's link detector on Android
    // doesn't always linkify URLs containing `/api/` - tapping the
    // bubble then runs a Google search of the entire message instead
    // of opening the link. The body text already communicates the
    // collection name, so a generic OG preview is an acceptable
    // trade-off for guaranteed-tappable URLs.
    const viewLink = category?.category_id ? `${window.location.origin}/c/${category.category_id}` : '';
    const viewShareText = `Enjoy my "${category?.name}" collection on BrainSync`;
    const viewShareMessage = viewShareText + '\n' + viewLink;
    const canShareViewLink = visibility === 'unlisted' || visibility === 'public';
    const [viewLinkCopied, setViewLinkCopied] = useState(false);

    const handleCopyViewLink = () => {
        // Copy the message body + URL so paste-into-anywhere matches
        // the WhatsApp/Telegram share UX (text + small link preview).
        navigator.clipboard.writeText(viewShareMessage);
        setViewLinkCopied(true);
        toast.success('View link copied');
        setTimeout(() => setViewLinkCopied(false), 2000);
    };

    const shareLink = shareInfo?.share_token ? `${window.location.origin}/join/${shareInfo.share_token}` : '';
    const shareText = `Join my "${category?.name}" collection on BrainSync`;
    const shareMessage = shareText + '\n' + shareLink;

    // Social handlers always share the VIEW link - wide-net broadcasts
    // (WhatsApp, Telegram, X, Facebook, Instagram, email) should land
    // recipients on a no-friction read-only page. Direct invites use
    // the dedicated invite section's copy button.
    const handleShareWhatsApp = () => {
        window.location.href = `whatsapp://send?text=${encodeURIComponent(viewShareMessage)}`;
    };
    const handleShareTelegram = () => {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(viewLink)}&text=${encodeURIComponent(viewShareText)}`, '_blank');
    };
    const handleShareX = () => {
        window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(viewShareText)}&url=${encodeURIComponent(viewLink)}`, '_blank');
    };
    const handleShareEmail = () => {
        window.open(`mailto:?subject=${encodeURIComponent(viewShareText)}&body=${encodeURIComponent(viewShareMessage)}`, '_self');
    };
    const handleShareFacebook = () => {
        window.location.href = `fb-messenger://share/?link=${encodeURIComponent(viewLink)}`;
    };
    const handleShareInstagram = () => {
        navigator.clipboard.writeText(viewShareMessage);
        toast.success('Link copied - paste it in your Instagram DM');
        setTimeout(() => {
            window.open('https://www.instagram.com/direct/inbox/', '_blank');
        }, 400);
    };
    const handleNativeShare = async () => {
        try {
            // Pass the combined "text\nurl" string as the single `text`
            // field (omitting the `url` key) so WhatsApp / Telegram on
            // Android display the message body AND the link preview.
            // When `url` is passed separately, Android share intents
            // typically forward only the URL and drop the text.
            await navigator.share({ title: viewShareText, text: viewShareMessage });
        } catch (err) { /* User cancelled share - expected */ }
    };

    const handleUpdateMemberRole = async (userId, newRole) => {
        try {
            await fetch(`${API}/categories/${category.category_id}/members/${userId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ role: newRole }),
            });
            toast.success('Role updated');
            fetchShareInfo();
        } catch {
            toast.error('Failed to update role');
        }
    };

    const handleRemoveMember = async (userId, name) => {
        if (!window.confirm(`Remove ${name} from this collection?`)) return;
        try {
            await fetch(`${API}/categories/${category.category_id}/members/${userId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            toast.success(`${name} removed`);
            fetchShareInfo();
        } catch {
            toast.error('Failed to remove member');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-h-[85vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
                style={{
                    // Override the default w-[calc(100%-2rem)] with a
                    // viewport-relative width that also accounts for
                    // any vertical scrollbar pushing the layout. Inline
                    // styles win over class merges.
                    width: 'calc(100vw - 1.5rem)',
                    maxWidth: '28rem',
                }}
                data-testid="share-category-modal"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Users className="w-5 h-5" />
                        {`Share "${category?.name}"`}
                    </DialogTitle>
                </DialogHeader>

                {/* Discoverability summary - the actual three-state
                    toggle now lives in the Edit Collection modal so
                    there's one canonical place to change "what is
                    this?" attributes. Here we just SURFACE the current
                    posture (so the user knows whether the view link
                    below is reachable by outsiders) and offer a one-
                    tap path to flip it. */}
                <div className="py-2" data-testid="category-visibility-section">
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border bg-secondary/30">
                        <div className="flex items-center gap-2.5 min-w-0">
                            {visibility === 'public' && (
                                <Globe className="w-4 h-4 text-emerald-600 shrink-0" />
                            )}
                            {visibility === 'unlisted' && (
                                <Link2 className="w-4 h-4 text-sky-600 shrink-0" />
                            )}
                            {visibility === 'private' && (
                                <Lock className="w-4 h-4 text-zinc-500 shrink-0" />
                            )}
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground capitalize" data-testid="category-current-visibility">
                                    {visibility}
                                </p>
                                <p className="text-[11px] text-muted-foreground truncate">
                                    {visibility === 'public' && 'Discoverable on your profile'}
                                    {visibility === 'unlisted' && 'Anyone with the link'}
                                    {visibility === 'private' && 'Only you can see this'}
                                </p>
                            </div>
                        </div>
                        {onRequestEdit && (
                            <button
                                type="button"
                                onClick={() => { onOpenChange?.(false); onRequestEdit(category); }}
                                className="shrink-0 text-xs font-semibold text-primary hover:underline"
                                data-testid="category-edit-privacy-link"
                            >
                                Change
                            </button>
                        )}
                    </div>
                </div>

                {/* ── VIEW LINK ─────────────────────────────────────
                    Read-only share. Recipient lands on /c/{id}, sees
                    the bookmarks, no sign-up nag. Only enabled when
                    visibility is Unlisted or Public. */}
                <div className="border-t border-border my-1" />
                <div className="space-y-3 pt-3" data-testid="view-link-section">
                    <div className="flex items-baseline justify-between gap-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Globe className="w-3 h-3 inline mr-1 -mt-0.5" />
                            Share a view link
                        </p>
                        {canShareViewLink && (
                            <p className="text-[11px] text-muted-foreground">Read-only · no sign-up needed</p>
                        )}
                    </div>

                    {canShareViewLink ? (
                        <>
                            <div className="flex gap-2 min-w-0">
                                <div
                                    className="flex-1 min-w-0 px-3 py-2 text-xs bg-muted rounded-lg truncate leading-loose"
                                    data-testid="view-link-display"
                                >
                                    {viewLink}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCopyViewLink}
                                    className="shrink-0"
                                    data-testid="copy-view-link-btn"
                                >
                                    {viewLinkCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                </Button>
                            </div>
                            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-testid="share-social-buttons">
                                <button
                                    onClick={handleShareWhatsApp}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors"
                                    title="Share via WhatsApp"
                                    data-testid="share-whatsapp-btn"
                                >
                                    <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                </button>
                                <button
                                    onClick={handleShareTelegram}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#0088cc]/10 hover:bg-[#0088cc]/20 transition-colors"
                                    title="Share via Telegram"
                                    data-testid="share-telegram-btn"
                                >
                                    <svg className="w-5 h-5 text-[#0088cc]" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                                </button>
                                <button
                                    onClick={handleShareX}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-black/5 hover:bg-black/10 transition-colors"
                                    title="Share on X"
                                    data-testid="share-x-btn"
                                >
                                    <svg className="w-4 h-4 text-foreground" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                </button>
                                <button
                                    onClick={handleShareFacebook}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition-colors"
                                    title="Share on Facebook"
                                    data-testid="share-facebook-btn"
                                >
                                    <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                </button>
                                <button
                                    onClick={handleShareInstagram}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[#F58529]/10 via-[#DD2A7B]/10 to-[#8134AF]/10 hover:from-[#F58529]/20 hover:via-[#DD2A7B]/20 hover:to-[#8134AF]/20 transition-colors"
                                    title="Share via Instagram"
                                    data-testid="share-instagram-btn"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#ig-grad)"><defs><linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#F58529"/><stop offset="50%" stopColor="#DD2A7B"/><stop offset="100%" stopColor="#8134AF"/></linearGradient></defs><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                                </button>
                                <button
                                    onClick={handleShareEmail}
                                    className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors"
                                    title="Share via Email"
                                    data-testid="share-email-btn"
                                >
                                    <Mail className="w-4.5 h-4.5 text-muted-foreground" />
                                </button>
                                {typeof navigator.share === 'function' && (
                                    <button
                                        onClick={handleNativeShare}
                                        className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-primary/10 hover:bg-primary/20 transition-colors"
                                        title="More sharing options"
                                        data-testid="share-native-btn"
                                    >
                                        <Share2 className="w-4.5 h-4.5 text-primary" />
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="rounded-xl border border-dashed border-border bg-muted/30 px-3 py-3 text-xs text-muted-foreground" data-testid="view-link-disabled-hint">
                            <Lock className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-muted-foreground" />
                            This collection is private. Switch to <span className="font-medium text-foreground">Unlisted</span> or <span className="font-medium text-foreground">Public</span> above to enable a view link.
                        </div>
                    )}
                </div>

                {/* ── INVITE PEOPLE TO COLLABORATE ──────────────────
                    Generates a /join/{token} link. Recipients land on
                    a "Join this collection" page and become members
                    (with optional edit permission). Pro feature. */}
                <div className="border-t border-border my-1 mt-4" />

                <div className="pt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                        <Users className="w-3 h-3 inline mr-1 -mt-0.5" />
                        Invite people to collaborate
                    </p>
                </div>

                {!isPro ? (
                    <div className="py-6 text-center" data-testid="share-pro-required">
                        <Crown className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground mb-1">Collaboration requires a Pro subscription</p>
                        <p className="text-xs text-muted-foreground">Upgrade to invite others to add bookmarks to this collection</p>
                    </div>
                ) : !shareInfo?.is_shared ? (
                    <div className="space-y-4 py-2">
                        <p className="text-sm text-muted-foreground">
                            Create an invite link. Recipients become members of this collection and can optionally add bookmarks too.
                        </p>
                        <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                            <div>
                                <p className="text-sm font-medium">Allow editing</p>
                                <p className="text-xs text-muted-foreground">Members can add bookmarks</p>
                            </div>
                            <Switch
                                checked={editPermission}
                                onCheckedChange={setEditPermission}
                                data-testid="share-edit-toggle"
                            />
                        </div>
                        <Button
                            onClick={handleCreateShare}
                            disabled={loading}
                            className="w-full"
                            data-testid="create-share-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                            Create invite link
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        {/* Invite link - copy-only, no social buttons (those
                            sit under the view-link section above) */}
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Invite link</label>
                            <div className="flex gap-2 min-w-0">
                                <div
                                    className="flex-1 min-w-0 px-3 py-2 text-xs bg-muted rounded-lg truncate leading-loose"
                                    data-testid="share-link-display"
                                >
                                    {window.location.origin}/join/{shareInfo.share_token}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCopyLink}
                                    className="shrink-0"
                                    data-testid="copy-share-link-btn"
                                >
                                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                                Recipients will be asked to sign in and join this collection.
                            </p>
                        </div>

                        {/* Default Permission Toggle */}
                        <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                            <div>
                                <p className="text-sm font-medium">Allow editing</p>
                                <p className="text-xs text-muted-foreground">New members can add bookmarks</p>
                            </div>
                            <Switch
                                checked={editPermission}
                                onCheckedChange={handleUpdatePermission}
                                data-testid="share-edit-toggle"
                            />
                        </div>

                        {/* Members List */}
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Members ({shareInfo.members?.length || 0})
                            </label>
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                                {shareInfo.members?.map((member) => (
                                    <div
                                        key={member.user_id}
                                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50"
                                        data-testid={`member-${member.user_id}`}
                                    >
                                        {member.picture ? (
                                            <img src={member.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                                                {(member.name || '?')[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium truncate">{member.name}</p>
                                            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                                        </div>
                                        {member.role === 'owner' ? (
                                            <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                                <ShieldCheck className="w-3 h-3" />Owner
                                            </span>
                                        ) : shareInfo.is_owner ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleUpdateMemberRole(member.user_id, member.role === 'editor' ? 'viewer' : 'editor')}
                                                    className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                                    title={member.role === 'editor' ? 'Switch to view-only' : 'Allow editing'}
                                                    data-testid={`toggle-role-${member.user_id}`}
                                                >
                                                    {member.role === 'editor' ? <Pencil className="w-3.5 h-3.5 text-blue-500" /> : <Eye className="w-3.5 h-3.5" />}
                                                </button>
                                                <button
                                                    onClick={() => handleRemoveMember(member.user_id, member.name)}
                                                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                                    title="Remove member"
                                                    data-testid={`remove-member-${member.user_id}`}
                                                >
                                                    <UserMinus className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground capitalize">{member.role}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Revoke Button */}
                        {shareInfo.is_owner && (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleRevokeShare}
                                disabled={loading}
                                className="w-full"
                                data-testid="revoke-share-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Revoke invites
                            </Button>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default ShareCategoryModal;
